'use client';

import { useEffect, useRef } from 'react';
import { Modal, ModalContent, useDisclosure } from '@heroui/modal';
import { Button, Spinner } from '@heroui/react';
import EnergyCalculator from '@/app/components/energy-calculator';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import useOfferComparisonStore from '@/app/store/use-offer-comparison-store';
import { useTranslations } from 'next-intl';

type MobileEnergyCalculatorProps = {
  apiKey: string;
  isBusinessEnabled: boolean;
  country: string;
  serviceBaseUrl?: string;
};
export default function MobileEnergyCalculator({
  apiKey,
  isBusinessEnabled,
  country,
  serviceBaseUrl,
}: MobileEnergyCalculatorProps) {
  const t = useTranslations('offerCalculator');
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const isInvoiceLoading = useInvoiceUploadStore((state) => state.isLoading);
  const isOfferLoading = useOfferComparisonStore((state) => state.isLoading);
  const isLoading = isInvoiceLoading || isOfferLoading;
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const energyCalculatorRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const hasOffer = useOfferComparisonStore((state) => state.offer) != null;

  useEffect(() => {
    if (isOpen && isLoading) {
      onOpenChange();
    }
  }, [isLoading]);

  const handleButtonPress = () => {
    window.scrollTo({
      top: document.body.scrollTop,
      behavior: 'smooth',
    });
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      onOpen();
    }, 30);
  };

  useEffect(() => {
    if (!isOpen || !modalRef) return;
    const viewportHeight = window.innerHeight;
    const searchBarHeight =
      viewportHeight - document.documentElement.clientHeight;
    const modal = modalRef.current!.getBoundingClientRect();
    const isUploadInvoiceVisible =
      viewportHeight >= modal.height + searchBarHeight;

    if (!isUploadInvoiceVisible)
      energyCalculatorRef.current?.scrollIntoView({
        block: 'end',
        inline: 'nearest',
        behavior: 'instant',
      });
  }, [isOpen]);

  return (
    <div data-testid="mobileCalculatorContainer">
      <h1
        className={`${hasOffer ? 'pt-12 pb-6' : 'flex items-center justify-center'} text-center`}
        style={!hasOffer ? { height: 'calc(100vh - 316px)' } : {}}
        data-testid="mobileEnergyCalculatorPageTitle" // 90 navigation 60 banner 74 upload 40 margins 52 button
      >
        {t('title')}
      </h1>
      <Button
        data-testid="energyCalculatorScreenButtonEdit"
        isDisabled={isLoading}
        className="btn-primary btn-padded w-full h-full"
        onPress={handleButtonPress}
      >
        {isLoading ? <Spinner /> : <span>{t('cta.mobile')}</span>}
      </Button>
      <Modal
        isDismissable={false}
        scrollBehavior="outside"
        ref={modalRef}
        className="bg-[var(--color-card-accent)]"
        placement="bottom"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{
          closeButton: 'custom-close-button',
          wrapper: 'min-h-full',
        }}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.3,
              },
            },
            exit: {
              y: 700,
              opacity: 1,
              transition: {
                duration: 0.4,
              },
            },
            initial: {
              y: 500,
              opacity: 1,
              transition: {
                duration: 0.2,
              },
            },
          },
          initial: 'initial',
          animate: 'enter',
          exit: 'exit',
        }}
      >
        <ModalContent>
          <div
            ref={energyCalculatorRef}
            className="pb-[52px]"
            data-testid="mobileEnergyCalculatorInputPopup"
          >
            <EnergyCalculator
              apiKey={apiKey}
              isBusinessEnabled={isBusinessEnabled}
              country={country}
              serviceBaseUrl={serviceBaseUrl}
            />
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}
